import { Suspense, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import {
  SettingsDialogContent,
  SettingsDialogFooter,
  SettingsDialogHeader,
} from "@/components/ui/settings-dialog";
import { canUseShellTerminal } from "@/lib/logout";
import { Toaster } from "@/components/ui/sonner";
import { Terminal } from "@/components/ui/icons";
// Eager (not lazy): the overlay registers the `ano:open-agent-setup` listener
// on mount, so it must be present before the Inbox card can be clicked — a
// lazy chunk that hasn't loaded yet would drop the (fire-and-forget) event.
import { ConnectAgentDialog } from "@/components/onboarding/ConnectAgentDialog";
import {
  CommandPalette,
  KeyboardShortcutsDialog,
  ListCreateDialog,
  ListItemCreateDialog,
} from "./lazy-imports";

interface UserLike {
  id: string;
  display_name?: string | null;
}

export interface AppDialogsTerminalProps {
  terminalWorkspaceId: string | null;
  terminalOpen: boolean;
  terminalWidth: number;
  handleToggleTerminal: () => void | Promise<void>;
}

export interface AppDialogsCommandPaletteProps {
  showCommandPalette: boolean;
  setShowCommandPalette: (v: boolean) => void;
  workspaceList: ReadonlyArray<{ id: string; slug?: string | null }>;
  navigateToSubdomain: (slug: string) => boolean;
  setScope: (s: string) => void;
  openChannelWithContext: (
    channelId: string,
    opts?: {
      workspaceId?: string | null;
      targetMessageId?: string;
      targetThreadId?: string;
    },
  ) => void;
}

export interface AppDialogsMembershipLostProps {
  membershipLostWorkspace: string | null;
  setMembershipLostWorkspace: (name: string | null) => void;
  setSelectedChannel: (id: string | null) => void;
  setShowLists: (v: boolean) => void;
  setShowCoworkers: (v: boolean) => void;
  setShowSkills: (v: boolean) => void;
  setShowConnectedTools: (v: boolean) => void;
  setShowLibrary: (v: boolean) => void;
  setSelectedListId: (id: string | null) => void;
  setScope: (s: string) => void;
  navigateToSubdomain: (slug: string) => boolean;
}

export interface AppDialogsListProps {
  selectedWorkspace: string | null;
  showListItemCreate: boolean;
  setShowListItemCreate: (v: boolean) => void;
  listItemCreatePreselect: string | undefined;
  setListItemCreatePreselect: (id: string | undefined) => void;
  setSelectedListId_FromCreate: (id: string) => void;
  showListCreate: boolean;
  setShowListCreate: (v: boolean) => void;
}

interface AppDialogsProps {
  user: UserLike | null | undefined;
  showChat: boolean;

  terminal: AppDialogsTerminalProps;
  commandPalette: AppDialogsCommandPaletteProps;
  membershipLost: AppDialogsMembershipLostProps;
  list: AppDialogsListProps;

  // Keyboard shortcuts
  showKeyboardShortcuts: boolean;
  setShowKeyboardShortcuts: (v: boolean) => void;
}

/**
 * Render-only — every overlay UI surface that lives outside the main
 * app shell flexbox. Owns no state.
 */
export function AppDialogs({
  user,
  showChat,
  terminal,
  commandPalette,
  membershipLost,
  list,
  showKeyboardShortcuts,
  setShowKeyboardShortcuts,
}: AppDialogsProps) {
  const {
    terminalWorkspaceId,
    terminalOpen,
    terminalWidth,
    handleToggleTerminal,
  } = terminal;
  const {
    showCommandPalette,
    setShowCommandPalette,
    workspaceList,
    navigateToSubdomain,
    setScope,
    openChannelWithContext,
  } = commandPalette;
  const {
    membershipLostWorkspace,
    setMembershipLostWorkspace,
    setSelectedChannel,
    setShowLists,
    setShowCoworkers,
    setShowSkills,
    setShowConnectedTools,
    setShowLibrary,
    setSelectedListId,
  } = membershipLost;
  const {
    selectedWorkspace,
    showListItemCreate,
    setShowListItemCreate,
    listItemCreatePreselect,
    setListItemCreatePreselect,
    setSelectedListId_FromCreate,
    showListCreate,
    setShowListCreate,
  } = list;

  // Hide the floating terminal-toggle button while the connect-agent takeover
  // is up — it would otherwise hover over the surface.
  const [agentSetupOpen, setAgentSetupOpen] = useState(false);

  return (
    <>
      {/* Terminal toggle button — always visible while in chat so the
       *  toggle affordance is preserved both ways (open / collapse).
       *  - Closed: parked at right=20 over the message area's right edge.
       *  - Open: parked just inside the terminal's left edge, overlapping
       *    the resize gutter. Button must sit above the gutter (z-40) so
       *    its own area is clickable instead of starting a resize drag. */}
      {canUseShellTerminal() &&
        terminalWorkspaceId &&
        showChat &&
        !agentSetupOpen && (
          <Button
            type="button"
            variant="outline"
            size="icon"
            onClick={handleToggleTerminal}
            // `data-on-shell-surface` flips the styling onto the Shell's
            // (potentially dark) surface when the Shell is open. Without
            // this, on matcha2-light the cream `bg-card` circle floats
            // against the dark Shell edge — visually broken. Theme CSS
            // targets the attribute to swap to terminal-bg/terminal-fg.
            data-on-shell-surface={terminalOpen ? "true" : undefined}
            className="fixed top-[140px] z-50 h-12 w-12 rounded-full border-border bg-card text-muted-foreground shadow-xl terminal-button-expanded hover:text-foreground"
            style={{
              right: terminalOpen ? `${terminalWidth - 24}px` : "20px",
            }}
            title={terminalOpen ? "Collapse terminal" : "Open terminal"}
            aria-label={terminalOpen ? "Collapse terminal" : "Open terminal"}
          >
            <Terminal size={18} className="text-info" />
          </Button>
        )}

      <Suspense
        fallback={
          showCommandPalette ? (
            <div
              className="fixed inset-0 z-40 bg-overlay/25"
              aria-hidden="true"
            />
          ) : null
        }
      >
        <CommandPalette
          openChannelWithContext={openChannelWithContext}
          onSelectWorkspace={(id: string | null) => {
            if (id) {
              const ws = workspaceList.find((w) => w.id === id);
              if (ws?.slug && navigateToSubdomain(ws.slug)) return;
              setScope(id);
            }
          }}
          open={showCommandPalette}
          onOpenChange={setShowCommandPalette}
        />
      </Suspense>

      <Dialog
        open={!!membershipLostWorkspace}
        onOpenChange={(open) => {
          if (!open) setMembershipLostWorkspace(null);
        }}
      >
        <SettingsDialogContent size="sm" className="text-center">
          <SettingsDialogHeader
            title="Membership lost"
            description={
              <>
                You are no longer a member of{" "}
                <span className="font-semibold">{membershipLostWorkspace}</span>
              </>
            }
          />
          <SettingsDialogFooter className="justify-center sm:justify-center">
            <Button
              autoFocus
              onClick={() => {
                // Persist removal notice for the Home banner (keyed per user)
                try {
                  const key = `ano:removed-workspaces:${user?.id}`;
                  if (user?.id) {
                    const raw = JSON.parse(localStorage.getItem(key) || "[]");
                    const existing = Array.isArray(raw)
                      ? raw.filter((x): x is string => typeof x === "string")
                      : [];
                    if (
                      membershipLostWorkspace &&
                      !existing.includes(membershipLostWorkspace)
                    ) {
                      existing.push(membershipLostWorkspace);
                      localStorage.setItem(key, JSON.stringify(existing));
                    }
                  }
                } catch {
                  // ignore localStorage errors
                }
                setMembershipLostWorkspace(null);
                if (navigateToSubdomain("home")) return;
                setScope("home");
                setSelectedChannel(null);
                setShowLists(false);
                setShowCoworkers(false);
                setShowSkills(false);
                setShowConnectedTools(false);
                setShowLibrary(false);
                setSelectedListId(null);
              }}
            >
              Go Home
            </Button>
          </SettingsDialogFooter>
        </SettingsDialogContent>
      </Dialog>

      <Suspense>
        {selectedWorkspace && (
          <ListItemCreateDialog
            open={showListItemCreate}
            onOpenChange={(v) => {
              setShowListItemCreate(v);
              if (!v) setListItemCreatePreselect(undefined);
            }}
            workspaceId={selectedWorkspace}
            preselectedListId={listItemCreatePreselect}
            onCreated={(listId: string) => {
              setSelectedListId_FromCreate(listId);
              setShowLists(false);
              setShowCoworkers(false);
              setShowSkills(false);
              setShowConnectedTools(false);
              setShowLibrary(false);
            }}
            onCreateList={() => setShowListCreate(true)}
          />
        )}
      </Suspense>

      {showListCreate && selectedWorkspace && user && (
        <Suspense>
          <ListCreateDialog
            open={showListCreate}
            onOpenChange={setShowListCreate}
            workspaceId={selectedWorkspace}
            userId={user.id}
            onCreated={(listId: string) => {
              setSelectedListId_FromCreate(listId);
              setShowLists(false);
              setShowCoworkers(false);
              setShowSkills(false);
              setShowConnectedTools(false);
              setShowLibrary(false);
            }}
          />
        </Suspense>
      )}

      {user?.id && (
        <>
          {/* Eager + always mounted: opened on demand from the Inbox "Set up
              your code agent" card (ano:open-agent-setup) — Electron-gated
              internally. The listener must exist before the card is clicked. */}
          <ConnectAgentDialog
            userId={user.id}
            workspaceId={selectedWorkspace}
            firstName={user.display_name?.split(" ")[0]}
            // The setup surface fills from the left edge up to the terminal's
            // left edge — no dead gap. The terminal is a flex sibling docked
            // right at `terminalWidth`, so the surface's right inset matches.
            terminalOpen={terminalOpen}
            terminalWidth={terminalWidth}
            onOpenChange={setAgentSetupOpen}
          />
        </>
      )}
      {showKeyboardShortcuts && (
        <Suspense fallback={null}>
          <KeyboardShortcutsDialog
            open={showKeyboardShortcuts}
            onOpenChange={setShowKeyboardShortcuts}
          />
        </Suspense>
      )}
      <Toaster />
    </>
  );
}
